import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { CountryDto, ScoreRecipientInstitutionDto, ScoreRecipientRegDto, TestScoresOptionDto, UserAccountRegDto } from 'src/app/core/dtos/dto';
import { BrandEnum, TestTypeEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { RegistrationManagementService } from 'src/app/core/services/registration-management.service';
import { ShippingPreference } from '../shipping-preference-option';


@Component({
  selector: 'app-score-report-options-selector',
  templateUrl: './score-report-options-selector.component.html',
  styleUrls: ['./score-report-options-selector.component.scss']
})
export class ScoreReportOptionsSelectorComponent implements OnInit {
  @ViewChild('confirmRemoveModal') confirmRemoveModal: ModalComponent;

  @Input() regInfo: UserAccountRegDto;
  @Input() testTypeId: number;
  @Input() regInProgress: boolean;
  @Input() storeTestScoresOption: TestScoresOptionDto;
  @Input() isWebDelivery: boolean;
  @Input() showIRCCOnNewInstitutions: boolean;

  userForm: FormGroup;
  country: CountryDto;
  countries: CountryDto[] = [];
  institutions: ScoreRecipientRegDto[];
  activeInstitutions: ScoreRecipientInstitutionDto[] = [];
  testScoresOption: TestScoresOptionDto;
  testPurposeInstitution: ScoreRecipientRegDto;
  submitted: boolean;
  brandName: string;
  brandId: BrandEnum;
  celpipcoreUsersUrl: string;
  newScoreRecipientReg: ScoreRecipientRegDto;
  shipmentPreferenceUpdated: boolean;
  scoreRecipientsUpdated: boolean;
  shippingPreferences: ShippingPreference[] = [];
  currencyCode: string;
  totalAmount = 0;
  maxInstitutionCount = 5;

  get f() { return this.userForm.controls; }

  constructor(
    private configHelper: ConfigHelper,
    private formBuilder: FormBuilder,
    private countryRegionService: CountryRegionService,
    private toastService: HotToastService,
    private registrationManagementService: RegistrationManagementService
  ) { }

  ngOnInit(): void {
    this.populateRegWithDefault();
    this.brandName = this.configHelper.getBrandName();
    this.brandId = this.configHelper.getBrandId();
    this.testPurposeInstitution = null;
    this.currencyCode = "CAD";
    this.celpipcoreUsersUrl = "https://www.CELPIP.ca/take-CELPIP/score-users/";
    this.userForm = this.formBuilder.group({
      country: [],
      institutions: [[]],
      scorelevel: [],
    });

    this.getCountries(this.testTypeId);
    this.registrationManagementService.getReceivingInstitutionByTestPurpose(this.regInfo?.regId ? this.regInfo?.regId : 0)
      .subscribe(
        result => {
          if (result?.content.institutionId > 0 && this.institutions?.length < 1) {
            this.institutions.push(result?.content);
          }
          this.testPurposeInstitution = result?.content;
        },
        () => {
        }
      );

    this.testScoresOption = JSON.parse(JSON.stringify(this.storeTestScoresOption));
    this.institutions = this.testScoresOption?.scoreRecipientRegs ? this.testScoresOption.scoreRecipientRegs : null;
  }

  public get TestTypeEnum() {
    return TestTypeEnum;
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  removeInstitution(removedInstitution: ScoreRecipientRegDto) {
    if (removedInstitution.expressShipping) {
      this.toastService.warning("This receiving institution cannot be removed as you have requested that the Official CAEL Score Report be sent through priority shipping. Please feel free to " +
        "<a target='_blank' href='https://www.cael.ca/contact/'> contact our office </a> if you have any questions.");
    }
    else if (this.testPurposeInstitution != null && removedInstitution.institutionId === this.testPurposeInstitution.institutionId) {
      this.toastService.warning("This receiving institution cannot be removed because it is for your test purpose selection.");
    }
    else {
      this.scoreRecipientsUpdated = true;
      this.confirmRemoveModal.clickPositive.subscribe(() => {
        const index = this.institutions.indexOf(removedInstitution);
        if (index > -1) {
          this.institutions.splice(index, 1);
        }
      });
      this.confirmRemoveModal.open();
    }
  }

  getCountries(testTypeId: number) {
    this.countryRegionService.getCountriesWithScoreRecipients(testTypeId).subscribe(
      result => {
        this.countries = result.content;
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }

      });
  }

  institutionSubmitHandler(scoreRecipient: ScoreRecipientRegDto) {
    if (this.institutions?.length < this.maxInstitutionCount) {
      this.submitted = true;
      //iterate through the keys and set them as dirty, this will set all the form values as invalid which lets the user more easily see what is missing
      Object.keys(this.userForm.controls).forEach((control: string) => {
        this.userForm.controls[control].markAsDirty();
      });

      if (this.brandId === BrandEnum.CELPIP && !scoreRecipient.institutionId) {
        this.toastService.warning("Please fill in required fields");
      }
      else if (this.brandId === BrandEnum.CAEL && (!scoreRecipient.institutionId || !scoreRecipient.academicLevel)) {
        this.toastService.warning("Please fill in required fields");
      }
      else {
        this.scoreRecipientsUpdated = true;
        this.institutions = [...this.institutions, scoreRecipient];
        // reset the form
        this.populateRegWithDefault();
        this.submitted = false;
        Object.keys(this.userForm.controls).forEach((control: string) => {
          this.userForm.controls[control].markAsPristine();
        });
      }
    }
    else {
      this.toastService.warning("Max number of institutions added");
    }
  }

  populateRegWithDefault() {
    this.newScoreRecipientReg = {
      id: new Date().getTime(),
      country: {
        abbr: "CA",
        addressInputFormat: 1,
        canHaveMiddleNameInLocalLanguage: true,
        canHaveNameInLocalLanguage: false,
        id: 38,
        localLanguageForName: "",
        name: "CANADA",
      },
      academicLevel: null,
      studentNumber: null,
      scoreRecipients: [],
      scoreRecipient: null,
      mailingAddress: null,
      institutionId: null,
      institutionName: null,
      dateCreatedUtc: new Date(),
      expressShipping: null,
      regId: null,
      other: false,
      isRegularMail: false,
      dateUpdatedUtc: null
    };
  }

  Validate(): boolean {
    const scoreRecipientIds = this.institutions.map(i => i.scoreRecipient?.id);
    if (!this.regInfo?.regId && this.regInProgress === false) {
      this.toastService.warning("This Registration is expired or does not exist");
      return false;
    }

    if (scoreRecipientIds.length > 1 && new Set(scoreRecipientIds).size !== scoreRecipientIds.length) {
      this.toastService.warning("One of the institutions has been added more than once. Please select a different institution or remove the duplicate institution.");
      return false;
    }

    if (!this.regInProgress) {
      this.testScoresOption.regId = this.regInfo?.regId;
    }
    this.testScoresOption.scoreRecipientRegs = this.institutions;
    this.testScoresOption.scoreReportMailingOptionId = this.institutions.length > 0 ? 3 : 1;
    this.testScoresOption.shippingRequiredScoreLevelId = null;
    this.testScoresOption.shipScoreReport = this.institutions.length > 0 ? true : false;
    return true;
  }

  shippingPreferenceHandler(shippingPreferenceOption: ShippingPreference) {
    this.shipmentPreferenceUpdated = true;
    const idx = this.shippingPreferences?.findIndex(s => s.institution.institutionId === shippingPreferenceOption.institution.institutionId);
    if (idx < 0 || this.shippingPreferences.length === 0) {
      this.shippingPreferences.push(shippingPreferenceOption);
    }
    else {
      this.shippingPreferences[idx] = shippingPreferenceOption;
    }
    this.calculateTotalAmount(this.shippingPreferences);
  }

  calculateTotalAmount(shippingPreferenceOptions: ShippingPreference[]) {
    this.totalAmount = shippingPreferenceOptions.map(s => s.price).reduce((a, b) => a + b, 0);
  }

}
